import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";

// Lazy load all page components for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const LinkDetail = lazy(() => import("./pages/LinkDetail"));
const DeepDiagnostics = lazy(() => import("./pages/DeepDiagnostics"));
const Alternatives = lazy(() => import("./pages/Alternatives"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Docs = lazy(() => import("./pages/Docs"));
const APIDocs = lazy(() => import("./pages/APIDocs"));
const WebhookDocs = lazy(() => import("./pages/WebhookDocs"));
const Developer = lazy(() => import("./pages/Developer"));
const Settings = lazy(() => import("./pages/Settings"));
const AlertSettings = lazy(() => import("./pages/AlertSettings"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Welcome = lazy(() => import("./pages/Welcome"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AffiliateCrawler = lazy(() => import("./pages/AffiliateCrawler"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient();

// Import cookie consent component
import CookieConsent from "./components/CookieConsent";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <div className="animate-fade-in">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/link/:id" element={<LinkDetail />} />
              <Route path="/link/:id/diagnostics" element={<DeepDiagnostics />} />
              <Route path="/link/:id/alternatives" element={<Alternatives />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/docs/api" element={<APIDocs />} />
              <Route path="/docs/webhooks" element={<WebhookDocs />} />
              <Route path="/developer" element={<Developer />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/billing" element={<Settings />} />
              <Route path="/settings/alerts" element={<AlertSettings />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/affiliate-crawler" element={<AffiliateCrawler />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </div>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
